import { useMemo, useEffect, useState } from "react";
import { MagnifyingGlassIcon, SparklesIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import WatchlistBuilderPane from "./WatchlistBuilderPane";
import DescribeItPanel from "./DescribeItPanel";
import type { WatchlistDraft } from "./DescribeItPanel";
import BuilderStart from "./BuilderStart";
import { conciergeEnabled } from "@/onboarding/concierge/api";
import { useWatchlistPush } from "@/demo-guide/useWatchlistPush";
import { useSearchParams } from "react-router-dom";
import { isPrebuiltWatchlist } from "../prebuiltWatchlists";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { formatListName } from "@/shared/utils/formatListName";
import type {
  UseWatchlistCreatorReturn,
  EditableWatchlist,
} from "../hooks/useWatchlistCreator";
import type { FilterDraft } from "../hooks/useFilterBuilder";

interface BuilderCanvasProps {
  creator: UseWatchlistCreatorReturn;
  onOpenInBuilder: (draft: FilterDraft) => void;
  /**
   * Optional, and separate from onOpenInBuilder on purpose: the concierge
   * route tolerates a no-op default, but the Advanced Builder link must not
   * render at all where there is nowhere for it to go.
   */
  onOpenAdvanced?: (draft: FilterDraft) => void;
  /** Seeds the builder from a single-watchlist concierge plan. */
  onOpenWatchlist?: (draft: WatchlistDraft) => void;
  onCreated: () => void;
  onCancel: () => void;
  onEditWatchlist: (item: EditableWatchlist) => void;
}

/** Region marker: uppercase label, hairline rule, fixed height. */
function PanelHeader({
  children,
  count,
}: {
  children: React.ReactNode;
  count?: number;
}) {
  return (
    <div className="flex h-9 shrink-0 items-center gap-2 border-b border-gray-700/50 bg-gray-800/40 px-3">
      <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-300">
        {children}
      </h2>
      {count !== undefined && (
        <span className="font-mono text-xs tabular-nums text-gray-500">{count}</span>
      )}
    </div>
  );
}

/**
 * The watchlist builder.
 *
 * Two panes rather than a centred column: building on the left as the page's
 * standing job, what you already own on the right as reference. The previous
 * version stacked everything into one 720px column on an 1800px screen and
 * gave eighteen existing watchlists more visual weight than the thing the page
 * exists to do — the eye goes to the largest mass, and that was the wrong one.
 *
 * The form is always present. Progressive disclosure applies to the Newsquawk
 * library, which stays folded away; hiding the form itself behind a click just
 * trades a wall of checkboxes for an empty canvas.
 */
export default function BuilderCanvas({
  creator,
  onOpenInBuilder,
  onOpenAdvanced,
  onOpenWatchlist,
  onCreated,
  onCancel,
  onEditWatchlist,
}: BuilderCanvasProps) {
  const [manualOpen, setManualOpen] = useState(false);

  /** Logged-out visitor sent here by the guided demo's last step. */
  const watchlistPush = useWatchlistPush();

  /**
   * ?describe=1 opens the assisted route, as if the card had been pressed.
   *
   * No longer used by the guided demo, which now asks the visitor to press the
   * card themselves — a rail button that opened this panel did nothing at all
   * on a second press, and doing it FOR them meant the run could complete
   * without the product being touched. See BUILD_ON_BUILDER.
   *
   * Kept as a deep link: it is the only way to land straight on the assisted
   * route, which is worth having for testing and for linking people to it.
   * Same shape as the ?create= handling in PortfolioBuilderBrowser: act once,
   * then clear the param so a refresh or a back-navigation does not
   * re-trigger it.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("describe") !== "1") return;
    setDescribeOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("describe");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [describeOpen, setDescribeOpen] = useState(false);
  const [search, setSearch] = useState("");

  const hasContent =
    creator.name.trim().length > 0 ||
    creator.tickers.length > 0 ||
    creator.keywords.length > 0 ||
    creator.selectedTags.length > 0 ||
    creator.importedWatchlistIds.size > 0;
  // Opening the form is an explicit step: pressing "Build it myself", or
  // arriving with content already in hand (an edit, or a concierge draft).
  const building = manualOpen || hasContent;

  /**
   * Every list the user owns, whatever store it lives in.
   *
   * This listed portfolios alone, so a tag or combined watchlist they had just
   * made was absent from the panel named after them — including everything the
   * concierge builds. Kind is carried only to route the click to the right
   * editor; the row itself says "watchlist" like all the others, except for
   * the Combined chip, which marks the one difference they can feel.
   */
  // Both already mounted by the page; this is a different view of cached data.
  const { watchlistsQuery } = useTagsWatchlistsCRUD();
  const { queriesQuery } = useHeadlineQueriesCRUD();
  const tagWatchlists = watchlistsQuery.data ?? [];
  const combinedWatchlists = queriesQuery.data ?? [];

  const mine = useMemo(() => {
    const rows: {
      key: string;
      name: string;
      combined?: boolean;
      item: EditableWatchlist;
    }[] = [];

    for (const p of creator.portfolios) {
      if (isPrebuiltWatchlist(p)) continue;
      rows.push({
        key: `p-${p.id}`,
        name: p.name,
        item: { kind: "watchlist", portfolio: p },
      });
    }

    for (const w of tagWatchlists) {
      if (w.owner_type !== "USER") continue;
      rows.push({
        key: `t-${w.id}`,
        name: w.name,
        item: { kind: "tag", watchlist: w },
      });
    }

    for (const q of combinedWatchlists) {
      if (q.owner_type !== "USER") continue;
      rows.push({
        key: `q-${q.id}`,
        name: q.queryName,
        combined: true,
        item: { kind: "combined", query: q },
      });
    }

    const query = search.trim().toLowerCase();
    const matched = query
      ? rows.filter((r) => r.name.toLowerCase().includes(query))
      : rows;
    const collator = new Intl.Collator(undefined, {
      sensitivity: "base",
      numeric: true,
    });
    return [...matched].sort((a, b) => collator.compare(a.name, b.name));
  }, [creator.portfolios, tagWatchlists, combinedWatchlists, search]);

  return (
    <div className="flex min-h-0 flex-1 divide-x divide-gray-700/50">
      {/* ── The work ─────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        {!building && describeOpen ? (
          /* The assisted route gets the same full container as the manual one,
             rather than being squeezed into the half-width card it was
             launched from. What it produces then lands in the ordinary
             builder, pre-filled — see `onOpenWatchlist`. */
          <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
            <div className="w-full px-5 py-5">
              <div className="flex items-center gap-2">
                <SparklesIcon className="size-4 shrink-0 text-gray-400" />
                <h2 className="flex-1 text-xs font-semibold uppercase tracking-[0.1em] text-gray-300">
                  Build it for me
                </h2>
                <button
                  type="button"
                  onClick={() => setDescribeOpen(false)}
                  className="cursor-pointer text-xs text-gray-500 transition-colors hover:text-gray-300"
                >
                  Cancel
                </button>
              </div>
              <div className="mt-4">
                <DescribeItPanel
                  variant="hero"
                  onOpenInBuilder={onOpenInBuilder}
                  onOpenWatchlist={onOpenWatchlist}
                  onCreated={onCreated}
                />
              </div>
            </div>
          </div>
        ) : !building ? (
          <BuilderStart
            onManual={() => setManualOpen(true)}
            onDescribe={conciergeEnabled ? () => setDescribeOpen(true) : undefined}
            demoPush={watchlistPush}
          />
        ) : (
        <>
        {/* No describe box here. The two ways in are a choice made on the way
            in, not a thing to re-offer above the form you already chose —
            sitting it on top of the manual builder made the page read as one
            path with a second one nagging over it. */}
        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          {/* Cancel closes the form as well as emptying it: `building` is
              `manualOpen || hasContent`, so clearing content alone left the
              first half true and the form on screen. */}
          <WatchlistBuilderPane
            creator={creator}
            onOpenAdvanced={onOpenAdvanced}
            onCombinedSaved={onCreated}
            onCancel={() => {
              setManualOpen(false);
              setDescribeOpen(false);
              onCancel();
            }}
          />

        </div>
        </>
        )}
      </div>

      {/* ── Reference: quieter by design, so it can't outweigh the work ── */}
      <aside className="hidden w-72 shrink-0 flex-col lg:flex xl:w-80">
        <PanelHeader count={mine.length}>Your watchlists</PanelHeader>

        {creator.portfolios.filter((p) => !isPrebuiltWatchlist(p)).length > 8 && (
          <div className="shrink-0 border-b border-gray-700/50 px-2 py-1.5">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-gray-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter"
                className="w-full rounded border border-gray-700 bg-gray-900 py-1 pl-7 pr-2 text-xs text-gray-200 placeholder:text-gray-500 focus:border-squawk-blue focus:outline-none"
              />
            </div>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          {mine.length === 0 ? (
            <p className="px-3 py-4 text-xs leading-relaxed text-gray-500">
              {search ? "No matches." : "Nothing here yet — your first one will appear here."}
            </p>
          ) : (
            mine.map((row) => (
              <button
                key={row.key}
                type="button"
                onClick={() => onEditWatchlist(row.item)}
                title={`Edit ${formatListName(row.name)}`}
                className="flex w-full cursor-pointer items-center gap-2 border-b border-gray-800/60 bg-transparent px-3 py-1.5 text-left transition-colors hover:bg-gray-800/60"
              >
                <span
                  aria-hidden
                  className="size-1.5 shrink-0 rounded-full"
                  style={{
                    backgroundColor:
                      row.item.kind === "watchlist"
                        ? row.item.portfolio.color
                        : "#4B5563",
                  }}
                />
                <span className="flex-1 truncate text-xs text-gray-300">
                  {formatListName(row.name)}
                </span>
                {row.combined && (
                  <span
                    title="Several watchlists joined with AND/OR rules"
                    className="shrink-0 rounded border border-gray-600 px-1 py-px text-2xs font-medium uppercase tracking-wide text-gray-400"
                  >
                    Combined
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </aside>
    </div>
  );
}
